import React from 'react';
import LoadingIndicator from '../components/shared/LoadingIndicator';
import ThemeWrapper from '../components/styles/ThemeWrapper/index.js';
import ErrorBoundary from '../components/errors/ErrorBoundary';
import Flexbox from 'flexbox-react';
import { Provider } from 'mobx-react';
import { Tooltip, Position, Icon, Tabs, Tab } from '@blueprintjs/core';
import axios from 'axios';
import Cookies from 'js-cookie';
import ScrollableDropdown from '../components/shared/ScrollableDropdown';
import ToastStore from '../stores/ToastStore';
import CurrentUserStore from '../stores/CurrentUserStore';
import ConfirmationDialog from './ConfirmationDialog';
import '../css/Review360View.css';

const RATING_SCALE = [
  { value: 1, label: 'Needs Improvement' },
  { value: 2, label: 'Fair' },
  { value: 3, label: 'Good' },
  { value: 4, label: 'Very Good' },
  { value: 5, label: 'Exceptional' }
];

export default class Review360View extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      reviewTab: 'self', // 'self' or 'others'
      isLoadingCycles: false,
      isLoadingSections: false,
      isLoadingUsers: false,

      goalCycleList: [],
      selectedGoalCycle: null,

      userList: [],
      selectedUser: null,

      sections: [],
      answers: {},
      isSubmitting: false,
      showSubmitConfirmDialog: false,

      selfFeedbackStatus: null,
      isLoadingSelfStatus: false
    };
  }

  componentDidMount() {
    this.fetchGoalCycles();
  }

  getApiUrl = (endpoint) => {
    const apiEnv = Cookies.get('apiEnv');
    const base = apiEnv ? apiEnv.replace(/\/+$/, '') : '';
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${base}${cleanEndpoint}`;
  };

  handleSwitchTab = (tab) => {
    if (this.state.reviewTab === tab) return;

    const { selectedGoalCycle } = this.state;

    this.setState({
      reviewTab: tab,
      answers: {},
      selectedUser: null,
      sections: [],
      selfFeedbackStatus: tab === 'self' ? null : this.state.selfFeedbackStatus,
      isLoadingSections: true
    }, () => {
      if (selectedGoalCycle?.id) {
        if (tab === 'self') {
          this.fetchSelfFeedbackStatus(selectedGoalCycle.id);
        } else {
          this.fetchAssignedUsers(selectedGoalCycle.id);
        }
        this.fetchSections(selectedGoalCycle.id);
      }
    });
  };

  fetchSelfFeedbackStatus = async (goalSettingId) => {
    if (!goalSettingId) return;

    const currentUserId = CurrentUserStore?.currentUser?.id || Cookies.get('user_id') || this.props.currentUserId;
    this.setState({
      isLoadingSelfStatus: true,
      selfFeedbackStatus: null,
      selectedUser: null,
      sections: []
    });

    try {
      const url = this.getApiUrl(`/feedback_sections/self_feedback_status?goal_setting_id=${goalSettingId}&user_id=${currentUserId}`);
      const response = await axios.get(url);

      const isSubmitted = Boolean(response?.data?.is_submitted);
      const isAvailable = Boolean(response?.data?.self_feedback_available);

      const selfUser = CurrentUserStore?.currentUser;
      const selfName = selfUser?.full_name || selfUser?.name || `${selfUser?.first_name || ''} ${selfUser?.last_name || ''}`.trim() || Cookies.get('user_name') || 'Self Review';
      const nameParts = selfName.split(' ').filter(Boolean);
      const initials = nameParts.length > 1
        ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
        : `${selfName.substring(0, 2)}`.toUpperCase();

      const activeUser = {
        id: String(currentUserId),
        name: selfName,
        initials: initials || 'ME',
        is_submitted: isSubmitted
      };

      this.setState({
        selfFeedbackStatus: {
          is_submitted: isSubmitted,
          self_feedback_available: isAvailable
        },
        selectedUser: activeUser,
        isLoadingSelfStatus: false
      });
    } catch (error) {
      console.error('Error fetching self feedback status:', error);
      this.setState({
        selfFeedbackStatus: { is_submitted: false, self_feedback_available: false },
        isLoadingSelfStatus: false
      });
    }
  };

  fetchGoalCycles = async () => {
    this.setState({ isLoadingCycles: true });
    try {
      const url = this.getApiUrl('/goal_settings/goal_cycle_list');
      const response = await axios.get(url);
      let cycles = [];

      if (Array.isArray(response?.data)) {
        cycles = response.data;
      } else if (Array.isArray(response?.data?.goal_settings)) {
        cycles = response.data.goal_settings;
      }

      const formattedCycles = cycles?.map(item => ({
        id: String(item.id || item.goal_setting_id),
        title: item.title || item.goal_setting_title || item.name || 'Goal Cycle'
      }));

      if (formattedCycles && formattedCycles.length > 0) {
        const initialCycle = formattedCycles[0];
        this.setState({
          goalCycleList: formattedCycles,
          selectedGoalCycle: initialCycle,
          isLoadingCycles: false
        });
        if (this.state.reviewTab === 'self') {
          this.fetchSelfFeedbackStatus(initialCycle.id);
        } else {
          this.fetchAssignedUsers(initialCycle.id);
        }
        this.fetchSections(initialCycle.id);
      } else {
        this.setState({
          goalCycleList: [],
          selectedGoalCycle: null,
          isLoadingCycles: false
        });
      }
    } catch (error) {
      console.error('Failed to load goal cycles:', error);
      this.setState({
        goalCycleList: [],
        selectedGoalCycle: null,
        isLoadingCycles: false
      });
    }
  };
  

  fetchAssignedUsers = async (goalSettingId, preserveUserId = null) => {
    if (!goalSettingId) return;

    const currentUserId = CurrentUserStore?.currentUser?.id || Cookies.get('user_id') || this.props.currentUserId;
    this.setState({ isLoadingUsers: true });

    try {
      const url = this.getApiUrl('/feedback_assignments/feedback_assigned_me');
      const response = await axios.post(url, {
        goal_setting_id: goalSettingId,
        user_id: currentUserId
      });

      let usersList = [];
      if (Array.isArray(response?.data?.users)) {
        usersList = response.data.users;
      } else if (Array.isArray(response?.data)) {
        usersList = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        usersList = response.data.data;
      }

      const formattedUsers = usersList?.map(u => {
        const fullName = u.full_name || u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'User';
        const nameParts = fullName.split(' ').filter(Boolean);
        const initials = nameParts.length > 1
          ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
          : `${fullName.substring(0, 2)}`.toUpperCase();

        return {
          id: String(u.id),
          name: fullName,
          initials: initials || 'U',
          is_submitted: Boolean(u.is_submitted || u.isSubmitted || u.submitted)
        };
      });

      let activeUser = null;

      if (this.state.reviewTab === 'self') {
        const selfMatched = formattedUsers?.find(u => String(u.id) === String(currentUserId));
        if (selfMatched) {
          activeUser = selfMatched;
        } else {
          const selfUser = CurrentUserStore?.currentUser;
          const selfName = selfUser?.full_name || selfUser?.name || `${selfUser?.first_name || ''} ${selfUser?.last_name || ''}`.trim() || Cookies.get('user_name') || 'Self Review';
          const nameParts = selfName.split(' ').filter(Boolean);
          const initials = nameParts.length > 1
            ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
            : `${selfName.substring(0, 2)}`.toUpperCase();

          activeUser = {
            id: String(currentUserId),
            name: selfName,
            initials: initials || '',
            is_submitted: false
          };
        }
      } else {
        const otherUsers = formattedUsers ? formattedUsers.filter(u => String(u.id) !== String(currentUserId)) : [];
        if (preserveUserId && formattedUsers) {
          const matched = formattedUsers.find(u => String(u.id) === String(preserveUserId));
          if (matched) {
            activeUser = matched;
          }
        }
        if (!activeUser) {
          activeUser = otherUsers.length > 0 ? otherUsers[0] : (formattedUsers && formattedUsers.length > 0 ? formattedUsers[0] : null);
        }
      }

      this.setState({
        userList: formattedUsers || [],
        selectedUser: activeUser,
        isLoadingUsers: false
      });
    } catch (error) {
      console.error('Error fetching assigned users:', error);
      this.setState({
        userList: [],
        selectedUser: null,
        isLoadingUsers: false
      });
    }
  };

  fetchSections = async (goalSettingId) => {
    if (!goalSettingId) return;

    this.setState({
      isLoadingSections: true,
      sections: []
    });
    try {
      const url = this.getApiUrl('/feedback_sections');
      const response = await axios.get(url, { params: { goal_setting_id: goalSettingId } });

      let rawList = [];
      if (Array.isArray(response?.data?.feedback_sections)) {
        rawList = response.data.feedback_sections;
      } else if (Array.isArray(response?.data)) {
        rawList = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        rawList = response.data.data;
      }

      const mappedSections = rawList?.map(sec => ({
        id: String(sec.id),
        title: sec.title || 'Untitled Section',
        questions: Array.isArray(sec.questions) ? sec.questions?.map(q => {
          const fq = q.feedback_question || {};
          const fqId = String(q.feedback_question_id || fq.id || '');
          return {
            id: String(q.id || fq.id),
            feedback_question_id: fqId,
            title: fq.title || fq.text || q.title || q.text || '',
            answer_type: fq.answer_type || q.answer_type || 'rating_scale'
          };
        }) : []
      }));

      const validSections = mappedSections ? mappedSections.filter(s => s.questions && s.questions.length > 0) : [];

      this.setState({
        sections: validSections,
        isLoadingSections: false
      });
    } catch (error) {
      console.error('Error fetching sections:', error);
      this.setState({
        sections: [],
        isLoadingSections: false
      });
    }
  };

  handleSelectUser = async (user) => {
    if (!user) return;
    const goalSettingId = this.state.selectedGoalCycle?.id;

    this.setState({
      selectedUser: user,
      answers: {}
    });

    if (goalSettingId) {
      await this.fetchAssignedUsers(goalSettingId, user.id);
      this.fetchSections(goalSettingId);
    }
  };

  handleSelectGoalCycle = (cycle) => {
    this.setState({
      selectedGoalCycle: cycle,
      selectedUser: null,
      answers: {},
      sections: [],
      selfFeedbackStatus: this.state.reviewTab === 'self' ? null : this.state.selfFeedbackStatus,
      isLoadingSections: true
    });
    if (this.state.reviewTab === 'self') {
      this.fetchSelfFeedbackStatus(cycle.id);
    } else {
      this.fetchAssignedUsers(cycle.id);
    }
    this.fetchSections(cycle.id);
  };

  handleRatingChange = (questionId, ratingValue) => {
    this.setState(prevState => ({
      answers: {
        ...prevState.answers,
        [questionId]: ratingValue
      }
    }));
  };

  handleTextChange = (questionId, textValue) => {
    this.setState(prevState => ({
      answers: {
        ...prevState.answers,
        [questionId]: textValue
      }
    }));
  };

  handleOpenConfirmDialog = () => {
    const { selectedUser } = this.state;
    if (!selectedUser) return;

    const { totalQuestions, answeredCount } = this.getQuestionStats();
    if (totalQuestions === 0 || answeredCount < totalQuestions) {
      ToastStore.showToast('All questions are mandatory. Please answer all questions before submitting.', 'warning');
      return;
    }

    this.setState({ showSubmitConfirmDialog: true });
  };

  handleCloseConfirmDialog = () => {
    this.setState({ showSubmitConfirmDialog: false });
  };

  handleConfirmSubmit = async () => {
    this.setState({ showSubmitConfirmDialog: false, isSubmitting: true });

    const { selectedUser, selectedGoalCycle, sections, answers } = this.state;
    if (!selectedUser || !selectedGoalCycle) {
      this.setState({ isSubmitting: false });
      return;
    }

    const targetUserId = selectedUser.id;
    const questionsPayload = [];

    sections?.forEach(sec => {
      sec.questions?.forEach(q => {
        const val = answers[q.id];
        const isRating = q.answer_type === 'rating_scale' || q.answer_type === 'rating';
        const targetFeedbackQuestionId = q.feedback_question_id;

        if (isRating) {
          if (val !== undefined && val !== null) {
            questionsPayload.push({
              feedback_question_id: targetFeedbackQuestionId,
              rating: val
            });
          }
        } else {
          if (val && typeof val === 'string' && val.trim().length > 0) {
            questionsPayload.push({
              feedback_question_id: targetFeedbackQuestionId,
              answer: val.trim()
            });
          }
        }
      });
    });

    try {
      const url = this.getApiUrl('/feedback_sections/feedback_submission');
      await axios.post(url, {
        user_id: targetUserId,
        goal_setting_id: selectedGoalCycle.id,
        questions: questionsPayload
      });

      ToastStore.showToast(`360° Feedback for ${selectedUser.name} submitted successfully!`, 'success');

      // Instantly trigger status/user API to fetch updated is_submitted state from server
      if (this.state.reviewTab === 'self') {
        await this.fetchSelfFeedbackStatus(selectedGoalCycle.id);
      } else {
        await this.fetchAssignedUsers(selectedGoalCycle.id, selectedUser.id);
      }
      this.setState({ isSubmitting: false, answers: {} });
    } catch (error) {
      console.error('Error submitting feedback form:', error);
      const errorMsg = error?.response?.data?.message || error?.response?.data?.error || 'Failed to submit feedback. Please try again.';
      ToastStore.showToast(errorMsg, 'error');
      this.setState({ isSubmitting: false });
    }
  };

  getQuestionStats = () => {
    const { sections, answers } = this.state;
    let totalQuestions = 0;
    let answeredCount = 0;

    sections?.forEach(sec => {
      sec.questions?.forEach(q => {
        totalQuestions += 1;
        const val = answers[q.id];
        if (q.answer_type === 'open_text' || q.answer_type === 'open_feedback') {
          if (val && typeof val === 'string' && val.trim().length > 0) {
            answeredCount += 1;
          }
        } else {
          if (val !== undefined && val !== null) {
            answeredCount += 1;
          }
        }
      });
    });

    return { totalQuestions, answeredCount };
  };

  render() {
    const {
      reviewTab,
      isLoadingCycles,
      isLoadingSections,
      isLoadingUsers,
      goalCycleList,
      selectedGoalCycle,
      userList,
      selectedUser,
      sections,
      answers,
      isSubmitting,
      showSubmitConfirmDialog,
      selfFeedbackStatus,
      isLoadingSelfStatus
    } = this.state;

    const currentUserId = CurrentUserStore?.currentUser?.id || Cookies.get('user_id') || this.props.currentUserId;
    const otherUsersList = userList ? userList.filter(u => String(u.id) !== String(currentUserId)) : [];
    const { totalQuestions, answeredCount } = this.getQuestionStats();
    const progressPercentage = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
    const userFirstName = selectedUser?.name ? selectedUser.name.split(' ')[0] : 'the employee';
    const isCurrentTabLoading = (
      (reviewTab === 'self' && (isLoadingSelfStatus || isLoadingSections)) ||
      (reviewTab === 'others' && (isLoadingUsers || isLoadingSections))
    );

    return (
      <Provider ToastStore={ToastStore}>
        <ThemeWrapper>
          <ErrorBoundary>
            <Flexbox flexDirection="column" className="review360-page-container">
              <div className="review360-main-content">
                {/* Top Header Bar */}
                <div className="review360-header">
                  <div className="review360-header-titles">
                    <h1 className="review360-main-title">360 Feedback Review</h1>
                    {selectedGoalCycle && (
                      <p className="review360-subtitle">
                        {selectedGoalCycle.title}
                      </p>
                    )}
                  </div>

                  {/* Filter Controls: Goal Cycle First, User Dropdown Second (Only in Feedback for Others tab) */}
                  <div className="review360-filter-controls">
                    <div className="filter-control-group">
                      {isLoadingCycles ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', fontSize: '13px', color: '#000000' }}>
                          <LoadingIndicator size={16} />
                          <span>Loading cycles...</span>
                        </div>
                      ) : (
                        <ScrollableDropdown
                          width={280}
                          label={selectedGoalCycle?.title || 'Select Goal Cycle'}
                          items={goalCycleList}
                          getKey={(item) => item.id}
                          getLabel={(item) => item.title}
                          enableSearch
                          onSelect={this.handleSelectGoalCycle}
                        />
                      )}
                    </div>

                    {reviewTab === 'others' && (
                      <div className="filter-control-group">
                        {isLoadingUsers ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', fontSize: '13px', color: '#000000' }}>
                            <LoadingIndicator size={16} />
                            <span>Loading users...</span>
                          </div>
                        ) : (
                          <ScrollableDropdown
                            width={240}
                            label={selectedUser?.name || 'Select User'}
                            items={otherUsersList.length > 0 ? otherUsersList : userList}
                            getKey={(item) => item.id}
                            getLabel={(item) => item.name}
                            enableSearch
                            onSelect={this.handleSelectUser}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Main Feedback Review Card */}
                <div className="review360-card">
                  {/* Native BlueprintJS Tabs Switcher */}
                  <Tabs
                    id="review360Tabs"
                    animate={true}
                    selectedTabId={reviewTab}
                    onChange={this.handleSwitchTab}
                    className="review360-bp-tabs"
                  >
                    <Tab
                      id="self"
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Icon icon="user" iconSize={16} />
                          <span>My Feedback</span>
                        </div>
                      }
                    />
                    <Tab
                      id="others"
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Icon icon="people" iconSize={16} />
                          <span>Feedback for Others</span>
                        </div>
                      }
                    />
                  </Tabs>
                  {isCurrentTabLoading ? (
                    <Flexbox flexGrow={1} flexDirection="column" justifyContent="center" alignItems="center" style={{ padding: '50px 0' }}>
                      <img
                        style={{ height: '35px', width: '35px' }}
                        src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif"
                        alt="Loading..."
                      />
                    </Flexbox>
                  ) : reviewTab === 'self' && selfFeedbackStatus && !selfFeedbackStatus.self_feedback_available ? (
                    <div className="review360-empty-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div className="empty-state-icon-wrap" style={{ display: 'inline-flex', padding: '16px', borderRadius: '50%', background: '#F4F5F7', marginBottom: '16px' }}>
                        <Icon icon="info-sign" iconSize={32} style={{ color: '#5E6C84' }} />
                      </div>
                      <h3 className="empty-state-title" style={{ fontSize: '18px', fontWeight: 600, color: '#172B4D' }}>
                        Your self-feedback isn’t open yet
                      </h3>
                      <p className="empty-state-desc" style={{ fontSize: '14px', color: '#5E6C84', maxWidth: '440px', margin: '0 auto', lineHeight: '1.5' }}>
                        This cycle’s self-feedback hasn’t started. Check back soon to complete it.
                      </p>
                    </div>
                  ) : selectedUser ? (
                    (reviewTab === 'self' ? selfFeedbackStatus?.is_submitted : selectedUser.is_submitted) ? (
                      <div className="review360-submitted-container">
                        <div className="submitted-success-icon-wrap">
                          <Icon icon="tick" iconSize={36} style={{ color: '#FFFFFF' }} />
                        </div>
                        <h2 className="submitted-success-title">Feedback Submitted</h2>
                        <p className="submitted-success-desc">
                          {reviewTab === 'self' ? (
                            'Thank you for sharing your thoughts. Your 360 review has been successfully submitted and securely added to the report.'
                          ) : (
                            <>
                              Thank you for sharing your thoughts. Your 360 feedback for <strong>{selectedUser.name}</strong> has been successfully submitted and added to the report.
                            </>
                          )}
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* User Profile Header */}
                        <div className="review360-user-header">
                          <div className="user-profile-info">
                            <div className="user-avatar-circle">
                              {selectedUser.initials}
                            </div>
                            <div className="user-details">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <div className="user-name">
                                  {selectedUser.name}
                                </div>
                                <span className={`review-type-badge ${reviewTab === 'self' ? 'self-badge' : 'others-badge'}`}>
                                  {reviewTab === 'self' ? 'Self Review' : 'Peer / Other Review'}
                                </span>
                              </div>
                              <div className="user-role-team">
                                {reviewTab === 'self'
                                  ? 'Complete your self-assessment review'
                                  : `Provide 360° feedback for ${selectedUser.name}`}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Dynamic Progress Indicator */}
                        <div className="review360-progress-container">
                          <div className="progress-bar-track">
                            <div
                              className="progress-bar-fill"
                              style={{ width: `${progressPercentage}%` }}
                            />
                          </div>
                          <div className="progress-text">
                            <span className="answered-highlight">{answeredCount} of {totalQuestions} questions answered</span> · your responses stay anonymous
                            {/* to {userFirstName} */}
                          </div>
                        </div>

                        {/* Dedicated Questions Scrollable Container */}
                        <div className="review360-questions-scroll-container">
                          {isLoadingSections ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                              <LoadingIndicator size={24} />
                            </div>
                          ) : sections.length > 0 ? (
                            sections?.map(section => (
                              <div key={section.id} className="review360-section">
                                <h3 className="section-title">{section.title}</h3>

                                {section?.questions?.map(q => {
                                  const questionTitleText = q.title.replace('{userName}', userFirstName);
                                  const isRatingScale = q.answer_type === 'rating_scale' || q.answer_type === 'rating';
                                  const isTitleLong = questionTitleText.length > 60;

                                  return (
                                    <div key={q.id} className="question-block">
                                      <Tooltip
                                        portalContainer={document.body}
                                        content={questionTitleText}
                                        disabled={!isTitleLong}
                                        position={Position.TOP_LEFT}
                                        hoverOpenDelay={100}
                                        hoverCloseDelay={50}
                                        openOnTargetFocus={false}
                                        popoverClassName="question-tooltip"
                                        targetClassName="question-title-target"
                                      >
                                        <p className="question-title line-clamp-2">{questionTitleText}</p>
                                      </Tooltip>

                                      {isRatingScale ? (
                                        <div className="rating-scale-grid">
                                          {RATING_SCALE?.map(opt => (
                                            <div
                                              key={opt.value}
                                              className={`rating-box ${answers[q.id] === opt.value ? 'selected' : ''}`}
                                              onClick={() => this.handleRatingChange(q.id, opt.value)}
                                            >
                                              <span className="rating-number">{opt.value}</span>
                                              <span className="rating-label">{opt.label}</span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <textarea
                                          className="feedback-textarea"
                                          placeholder="Write your feedback here — be specific and constructive."
                                          value={answers[q.id] || ''}
                                          onChange={(e) => this.handleTextChange(q.id, e.target.value)}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ))
                          ) : (
                            <div style={{ textAlign: 'center', padding: '30px', color: '#172B4D', fontSize: '14px', fontWeight: 400 }}>
                              No feedback questions available for the selected cycle.
                            </div>
                          )}
                        </div>

                        {/* Card Footer Actions */}
                        <div className="review360-card-footer">
                          <button
                            type="button"
                            className="btn-continue"
                            onClick={this.handleOpenConfirmDialog}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <LoadingIndicator size={14} />
                                <span>Saving...</span>
                              </>
                            ) : (
                              <span>Save</span>
                            )}
                          </button>
                        </div>
                      </>
                    )
                  ) : (
                    /* Empty state when zero assigned users */
                    <div className="review360-empty-state">
                      <h3 className="empty-state-title">No employees assigned for feedback</h3>
                      <p className="empty-state-desc">
                        You do not have any pending 360 feedback reviews assigned for this goal cycle.
                      </p>
                    </div>
                  )}
                </div>

                {/* Standard App ConfirmationDialog Component */}
                <ConfirmationDialog
                  isOpen={showSubmitConfirmDialog}
                  onClose={this.handleCloseConfirmDialog}
                  onConfirm={this.handleConfirmSubmit}
                  title="Confirm Submission"
                  message="Are you sure you want to submit your feedback review for "
                  itemName={selectedUser?.name || ''}
                  confirmText="Save"
                  confirmIntent="primary"
                  isSubmitting={isSubmitting}
                />
              </div>
            </Flexbox>
          </ErrorBoundary>
        </ThemeWrapper>
      </Provider>
    );
  }
}
